import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ChartBar, Package, Tag, ListBullets, Users, Gear, SignOut, Storefront, House } from '@phosphor-icons/react';
import { ADMIN } from '@/constants/testIds';
import ThemeApplier from '@/components/shop/ThemeApplier';
import NewOrderAlert from '@/components/admin/NewOrderAlert';

const links = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: ChartBar, testid: ADMIN.navDashboard },
  { to: '/admin/home', label: 'Home Page', icon: House, testid: 'nav-home-manager' },
  { to: '/admin/products', label: 'Products', icon: Package, testid: ADMIN.navProducts },
  { to: '/admin/categories', label: 'Categories', icon: Tag, testid: ADMIN.navCategories },
  { to: '/admin/orders', label: 'Orders', icon: ListBullets, testid: ADMIN.navOrders },
  { to: '/admin/customers', label: 'Customers', icon: Users, testid: ADMIN.navCustomers },
  { to: '/admin/sellers', label: 'Vendors', icon: Storefront, testid: 'nav-sellers' },
  { to: '/admin/settings', label: 'Settings', icon: Gear, testid: ADMIN.navSettings },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex bg-cream-100">
      <ThemeApplier />
      <NewOrderAlert />
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-olive-800 text-cream-100">
        <div className="px-5 py-5 flex items-center gap-2 border-b border-olive-700/60">
          <span className="w-9 h-9 rounded-xl bg-terracotta-600 grid place-items-center font-bold font-heading">Z</span>
          <div>
            <p className="font-heading font-bold">MyZippyKart</p>
            <p className="text-[10px] uppercase tracking-widest text-cream-200/60">Admin</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {links.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({isActive}) => `flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-medium transition-colors ${isActive ? 'bg-terracotta-600 text-white' : 'text-cream-100/80 hover:bg-olive-700'}`}
            >
              <Icon size={18} weight="duotone"/> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-olive-700/60 space-y-1">
          <a href="/" className="flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-medium text-cream-100/70 hover:bg-olive-700"><Storefront size={16}/> View store</a>
          <button data-testid={ADMIN.logoutBtn} onClick={() => { logout(); navigate('/admin/login'); }} className="w-full flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-medium text-cream-100/70 hover:bg-olive-700"><SignOut size={16}/> Logout</button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-olive-800 text-cream-100">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-terracotta-600 grid place-items-center font-bold font-heading">Z</span>
            <p className="font-heading">MyZippyKart · Admin</p>
          </div>
          <button data-testid={ADMIN.logoutBtn + '-mobile'} onClick={() => { logout(); navigate('/admin/login'); }} className="text-cream-100/80"><SignOut size={20}/></button>
        </div>
        <nav className="flex gap-1 px-2 py-2 overflow-x-auto scrollbar-hide border-t border-olive-700/60">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({isActive}) => `shrink-0 flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium ${isActive ? 'bg-terracotta-600 text-white' : 'bg-olive-700/60 text-cream-100/80'}`}>
              <Icon size={14} weight="duotone"/> {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 md:ml-0 mt-28 md:mt-0 min-w-0">
        <div className="p-4 sm:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
